import { Ref, forwardRef, useEffect, useImperativeHandle } from "react";
import { BaseActionEnum, BaseActions } from "../../../components/action-menu/ActionMenuItem";
import useDataFileImportListState from "./DataFileImportListState";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useDataFileImportConfigurations } from "../../../queries/data-file-import/UseDataFileImportConfigurations";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import DataFileImportListUtils, { DataFileImportActionEnum, DataFileImportItem, importTypes } from "./DataFileImportListUtils";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import { useTranslation } from "react-i18next";
import { Box, MenuItem, Stack, useTheme } from "@mui/material";
import { DataFileImportListItem } from "./DataFileImportListItem";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import NoData from "../../../components/no-data/NoData";
import { ImportTypeEnum } from "../../../services/data-file-import-data-service/Models";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import VirtualizedList from "../../../components/virtualized-list/VirtualizedList";
import OptionsSideSheet from "../../../components/options-side-sheet/OptionsSideSheet";
import SideSheetSelect from "../../../components/side-sheet/side-sheet-select/SideSheetSelect";
import SideSheetFilterInput from "../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import { useOrganizations } from "../../../queries/organizations/UseOrganizations";
import DataFileImportEditorDialog, { bindDataFileImportEditorDialog, useDataFileImportEditorDialogState } from "../data-file-import-configuration-editor/DataFileImportEditorDialog";
import { useDeleteDataFileImportConfiguration } from "../../../queries/data-file-import/UseDeleteDataFileImport";
import RenameConfigurationDialog, { bindRenameConfigurationDialog, useRenameConfigurationDialogState } from "../../../components/rename-configuration-dialog/RenameConfigurationDialog";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import _ from "lodash";
import { useSaveDataFileImportConfiguration } from "../../../queries/data-file-import/UseSaveDataFileImportConfiguration";
import RunImportDialog, { bindRunImportDialog, useRunImportDialogState } from "../run-import-dialog/RunImportDialog";
import DataFileImportDataService from "../../../services/data-file-import-data-service/DataFileImportDataService";
import { RestError } from "../../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../../contexts/app-notification-context/UseAppNotificationContext";
import { useProgressContext } from "../../../contexts/progress-context/ProgressContext";
import UUID from "../../../common/utilities/Uuid";
import { ActionTypeEnum, StatusEnum } from "../../../common/IExportNotificationMsg";
import { contains } from "../../../common/utilities/Contains";

export type DataFileImportListType = {
    executeAction: (action: DataFileImportActionEnum | BaseActions) => void;
};

export type DataFileImportListProps = {
    groupID: number;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

function DataFileImportList({
    groupID,
    setViewOptionState,
}: DataFileImportListProps, ref: Ref<unknown> | undefined) {

    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const theme = useTheme();
    const dataFileImportEditorDialog = useDataFileImportEditorDialogState();
    const renameConfigurationDialog = useRenameConfigurationDialogState();
    const runImportDialog = useRunImportDialogState();
    const [, setAppNotificationState ] = useAppNotificationContext();
    const [progressState, actionStarted] = useProgressContext();

    const {
        state,
        setFilterText,
        setImportType,
        addRunImportUID,
        removeRunImportUID,
        resetState,
    } = useDataFileImportListState();

    const {data, status, refetch } = useQueryNotifications(useDataFileImportConfigurations)({
        groupID: groupID,

    });

    const { mutate: deleteImportConfiguration } = useQueryNotifications(useDeleteDataFileImportConfiguration)({
        groupID: groupID,
        onSuccess: () => {
            acknowledge(t("deleteSuccessMessage", { itemName: t("dataTypeNames.dataFileImport") }));
        }
    });


    const {data: organizations} = useQueryNotifications(useOrganizations)({
    });

    const organization = organizations?.find(o => o.peerGroupID === groupID);

    const { mutate: saveImportConfiguration/*, status*/ } = useQueryNotifications(useSaveDataFileImportConfiguration)({
        groupID: groupID,

        onSuccess: async (returnData) => {
            acknowledge(t("dataFileImport.renameSuccessText", { 
                                old: data?.find(o => o.id === returnData.id)?.name,
                                new: returnData.name
                            }
                        ));

            await refetch();
        },
    });

    const configurations = DataFileImportListUtils.shapeData(data ?? [], state.filterText, state.importType);

    const nodata = status === "success" && (configurations?.length ?? 0) === 0; 

    useImperativeHandle<unknown, DataFileImportListType>(
        ref,
        () => ({
             executeAction: (action) => {onListAction(action)} 
        })
    );

    async function _edit(id: number) {

        const result = await dataFileImportEditorDialog.open({
            groupID,
            configurationID: id,
        });
        
        if (result.reason === 'save') {
            await refetch();
        }
    }

    function onAdd() {
        _edit(-1);
    } 

    function onEdit(item: DataFileImportItem) {
        _edit(item.id)
    }

    async function onDelete(item: DataFileImportItem) {
        const result = await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("dataTypeNames.dataFileImport") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });
         
        if ( result === "yes") {
            
            deleteImportConfiguration(item);
        }
    }

    async function onRename(item: DataFileImportItem) {

        const result = await renameConfigurationDialog.open({
            configurationName: item.name,
            configurationType: getObjectTypeName(eLynxObjectTypeEnum.DataFileImportConfiguration)
        });

        if (result.reason === "ok" && result.values) {

            const renamed = _.cloneDeep(item);
            renamed.name = result.values.configurationName;

            saveImportConfiguration(renamed);
        }
    
    }

    async function onRunConfiguration(item: DataFileImportItem) {

        const result = await runImportDialog.open({
            groupID: groupID,
            configuration: item
        });

        if (result.reason === "ok" && result.values) {


            // await this._verifyAndRunCsvImportFile({
            //     configID: this.selectedImportConfigItem.id,
            //     fileName: this._importFileStoreInfo.fileStoreFileName,
            //     objectOverride: this.configuration.importType == DataFileImportModels.ImportTypeEnum.Single &&
            //         this.selectedObject ? this.selectedObject : null,
            //     skipVerification: false
            // });            

            try {

                const uid = UUID.generate();

                await DataFileImportDataService.verifyAndRunCsvFile(groupID, {
                    configID: item.id,
                    fileName: result.values?.fileStoreInfo.fileStoreFileName,
                    objectOverride: item.importType === ImportTypeEnum.Single ?
                                result.values?.object : 
                                undefined,
                    skipVerification: false,
                    notificationID: uid
                });

                addRunImportUID({
                    Key: uid,
                    Value: item.id}
                );
        
                actionStarted({
                    uid: uid,
                    objectID: groupID,
                    objectType: eLynxObjectTypeEnum.Group,
                    title: `Running import ${item.name}`, 
                    notificationMsg: {
                        action: ActionTypeEnum.Import,
                        notificationID: uid,
                        status: StatusEnum.Started,
                        statusMessage: t('progress.sendingRequest'),
                        additionalInfo: '',
                        blobURI: '',
                        percentComplete: 0,
                        import: true
                    }
                });
        


            } catch (error) {
                const err =  error as RestError;
                   
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    message: err.message,
                    show: true,
                    error: err
                });
            }
        }
    }

    function onListAction(action: DataFileImportActionEnum | BaseActions, payload?: DataFileImportItem) {

        switch (action) {
            case BaseActionEnum.CreateNew:
                 onAdd();
                 break;

            case DataFileImportActionEnum.Edit:
                onEdit(payload as DataFileImportItem);
                break;

            case DataFileImportActionEnum.Delete:
                 onDelete(payload as DataFileImportItem);
                 break;

            case DataFileImportActionEnum.Rename:
                onRename(payload as DataFileImportItem);
            break;

            case DataFileImportActionEnum.Run:
                onRunConfiguration(payload as DataFileImportItem);
                break;

            case BaseActionEnum.Refresh:
                refetch();
                break;
        }
    }

    function renderItem(_index: number, item: DataFileImportItem) {
        return (
            <DataFileImportListItem
                key={`${item.id}`}
                item={item}
                onAction={onListAction}
            />
        );
    }

    function onFilterItem(item: DataFileImportItem): boolean {

        return !!contains(item.name, state.filterText)

    }

     useEffect(() => {
        if (setViewOptionState) {
            setViewOptionState(state.viewOptionState);
        }
    }, [
        state.viewOptionState,
        setViewOptionState
    ]);

    const progress = progressState.items.find(o => o.objectID === groupID && o.objectType === eLynxObjectTypeEnum.Group);

    //console.log(`datafileimport: ${progress?.notificationMsg.status}  import ${progress?.import}`);

    useEffect(() => {


        const showLog = async (fileStoreID: number) => {

            try {

                const logEntries = await DataFileImportDataService.getProcessImportLog(groupID, fileStoreID);

                if (logEntries) {

                    const body = DataFileImportListUtils.shapeImportLogs(logEntries);

                    await confirm({
                        dialogTitle: t("dataFileImport.processImportLog.title"),
                        yesButtonText: t("buttons.okay"),
                        confirmationBodyComponent: body,
                        showConfirmationText: false,
                        showYesButton: true,
                        showNoButton: false,
                        showCloseButton: false
                    });

                }

            } catch (error) {

                const err =  error as RestError;
                   
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    message: err.message,
                    show: true,
                    error: err
                });

            }

        }


        const runImportUID = state.runImportUIDs.find(o => o.Key === progress?.uid);

        if (runImportUID &&
            progress?.uid === runImportUID.Key &&
            (progress?.notificationMsg.status === StatusEnum.Completed ||
            progress?.notificationMsg.status === StatusEnum.Failed)) {

            removeRunImportUID(runImportUID);
            showLog(progress.notificationMsg.fileStoreID as number);

        }
    }, [
        progress,
        state.runImportUIDs,
        removeRunImportUID,
        confirm,
        groupID,
        setAppNotificationState,
        t
    ]);


    return (
        <LoadingZone isLoading={status === 'loading'}>
            <ContentContainer>
                <ContentSection>
                    {nodata ? 
                        <NoData
                            title={t("dataFileImport.nodata.title")}
                            text={<Stack
                                direction="column"
                                spacing={0.5}
                                >
                                <Box>
                                    {state.importType === ImportTypeEnum.None ?
                                    t("dataFileImport.nodata.textall", {
                                        org: organization?.displayText
                                    }) :
                                    t("dataFileImport.nodata.text", {
                                        importType: ImportTypeEnum[state.importType], 
                                        org: organization?.displayText
                                     })
                                    }
                                </Box>
                                <Box sx={{
                                    paddingTop: theme.spacing(2)
                                }}>
                                    {t("dataFileImport.nodata.instruction")}
                                </Box>
                            </Stack>
                            }
                        /> :
                        null
                    }
                
                    <VirtualizedList
                        items={configurations ?? []} 
                        renderItem={renderItem}
                        filter={onFilterItem}
                    />
                
                </ContentSection>

                <OptionsSideSheet 
                    onReset={resetState}
                >

                    <SideSheetSelect
                        label={t('dataFileImport.importTypeLabel')}
                        value={state.importType.toString()}
                        onChange={(value: string) => {
                            setImportType(+value);
                    }}>
                        {importTypes.map((o, index) => {
                            return (
                                <MenuItem key={index} value={o.value}>
                                    {o.text}
                                </MenuItem>
                            );
                        })}
                    </SideSheetSelect> 


                    
                    <SideSheetFilterInput
                        label={t("dataFileImport.filterBy")}
                        value={state.filterText}
                        onChange={(value: string) => {
                            setFilterText(value);
                        }}
                    />


                </OptionsSideSheet>
                
            </ContentContainer>
            <DataFileImportEditorDialog {...bindDataFileImportEditorDialog(dataFileImportEditorDialog)} />
            <RenameConfigurationDialog {...bindRenameConfigurationDialog(renameConfigurationDialog)} />
            <RunImportDialog {...bindRunImportDialog(runImportDialog)} />
        </LoadingZone>
    );
}

export default forwardRef(DataFileImportList);